// Incoming-call alerts (2026-10-22, docs/PWA.md): while the portal (browser tab or installed app) is open but
// minimized, behind other windows or in the background on a phone, a ringing softphone call shows a system
// notification "Incoming call from …". Tapping it brings the portal to the front; "Answer" also answers.
//   - shown through the service worker when there is one (Android Chrome allows no other way), else new Notification;
//   - only while the call rings: answered, rejected or hung up closes it;
//   - never when the portal is already in front (the softphone card shows the call);
//   - nothing when the app is closed: the softphone only exists while the portal is open.
import * as React from 'react';
import { Bell, BellOff } from 'lucide-react';
import { usePhone } from '@/store/phone';
import { Button } from '@/components/ui/button';
import { t } from '@/i18n';

const TAG = 'cc-incoming-call';
export const notificationsSupported = () => typeof window !== 'undefined' && 'Notification' in window;

function permission(): NotificationPermission | 'unsupported' {
  return notificationsSupported() ? Notification.permission : 'unsupported';
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

let shown: Notification | null = null;

async function show(who: string): Promise<void> {
  if (permission() !== 'granted') return;
  const icon = (document.querySelector('link[rel=apple-touch-icon]') as HTMLLinkElement | null)?.href || '/icons/icon-192.png';
  const opts: NotificationOptions & { renotify?: boolean; actions?: { action: string; title: string }[] } = {
    body: t('Incoming call from {{who}}', { who }), tag: TAG, icon, badge: '/icons/favicon-32.png',
    requireInteraction: true, renotify: true, data: { url: location.pathname },
  };
  const reg = await registration().catch(() => null);
  if (reg) {
    await reg.showNotification(t('Incoming call'), { ...opts, actions: [{ action: 'answer', title: t('Answer') }] } as NotificationOptions);
    return;
  }
  try {
    shown = new Notification(t('Incoming call'), opts);
    shown.onclick = () => { window.focus(); shown?.close(); };
  } catch { /* this browser only shows notifications from a service worker */ }
}

async function closeAll(): Promise<void> {
  shown?.close();
  shown = null;
  const reg = await registration().catch(() => null);
  for (const n of (await reg?.getNotifications({ tag: TAG }).catch(() => [])) ?? []) n.close();
}

const inFront = () => document.visibilityState === 'visible' && document.hasFocus();

let started = false;
// call once: follows the softphone and the clicks on the notification (sw.js posts them to the page)
export function startCallAlerts(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  usePhone.subscribe((s, prev) => {
    if (s.call === 'incoming' && prev.call !== 'incoming') {
      if (!inFront()) show(s.remote ?? t('unknown')).catch(() => {});
    } else if (s.call !== 'incoming' && prev.call === 'incoming') closeAll().catch(() => {});
  });
  navigator.serviceWorker?.addEventListener('message', (e) => {
    if (e.data?.type !== 'cc-call-alert') return;
    const p = usePhone.getState();
    if (e.data.action === 'answer' && p.call === 'incoming') p.answer().catch(() => {});
  });
}

// the button that asks for permission (browsers only allow asking from a click)
export function CallAlertsButton() {
  const [perm, setPerm] = React.useState(permission);
  React.useEffect(() => startCallAlerts(), []);
  if (perm === 'unsupported' || perm === 'granted') return null;
  if (perm === 'denied') {
    return (
      <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="call-alerts-blocked">
        <BellOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('Call alerts are blocked. Allow notifications for this site in the browser or phone settings.')}
      </div>
    );
  }
  return (
    <Button variant="outline" size="sm" data-testid="call-alerts-on" title={t('Show a notification when a call rings while the portal is in the background.')}
      onClick={async () => setPerm(await Notification.requestPermission().catch(() => permission()))}>
      <Bell /> {t('Turn on call alerts')}
    </Button>
  );
}
